import IntlMessageFormat from 'intl-messageformat';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { MESSAGES, type MessageKey } from './messages';
import { useSession } from '@/shared/auth';
import { supabase } from '@/lib/supabase';
import {
  DEFAULT_LOCALE,
  resolveFormatLocale,
  resolveLocale,
  storeLocale,
  type Locale,
} from './locales';
import { applyLocaleToFormats } from '@/shared/components/Grid/formats';

/** Values accepted by ICU placeholders. Never concatenate — interpolate. */
export type TranslateValues = Record<string, string | number | boolean | Date>;

export type Translate = (key: MessageKey, values?: TranslateValues) => string;

export interface I18nContextValue {
  locale: Locale;
  /** Full BCP-47 tag for `Intl` — may be `es-AR` while `locale` is `es`. */
  formatLocale: string;
  setLocale: (locale: Locale) => void;
  t: Translate;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string;
  formatCurrency: (value: number, currency: string) => string;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

const formatterCache = new Map<string, IntlMessageFormat>();

function translate(
  locale: Locale,
  formatLocale: string,
  key: MessageKey,
  values?: TranslateValues,
): string {
  const pattern = MESSAGES[locale][key] ?? MESSAGES[DEFAULT_LOCALE][key];

  if (pattern === undefined) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] missing key "${key}" in "${locale}"`);
    }
    return key;
  }

  // Plain strings are the common case; skip the ICU parser entirely.
  if (!pattern.includes('{')) return pattern;

  const cacheKey = `${formatLocale} ${key} ${pattern}`;
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new IntlMessageFormat(pattern, formatLocale);
    formatterCache.set(cacheKey, formatter);
  }

  const result = formatter.format(values);
  return typeof result === 'string' ? result : String(result);
}

interface Props {
  children: ReactNode;
  /** From `profiles.locale` once a session exists. Highest priority. */
  profileLocale?: string | null;
}

export function I18nProvider({ children, profileLocale }: Props) {
  const session = useSession();
  const [locale, setLocaleState] = useState<Locale>(() =>
    resolveLocale(profileLocale),
  );
  const profileWriteVersion = useRef(0);
  const userId = session.status === 'signedIn' ? session.session.user.id : null;

  useEffect(() => {
    if (!userId) return;

    let active = true;
    const requestVersion = profileWriteVersion.current;
    void supabase
      .from('profiles')
      .select('locale')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || requestVersion !== profileWriteVersion.current || typeof data?.locale !== 'string') return;
        const next = resolveLocale(data.locale);
        setLocaleState(next);
        storeLocale(next);
      });

    return () => {
      active = false;
    };
  }, [profileLocale, userId]);

  const formatLocale = useMemo(() => resolveFormatLocale(locale), [locale]);

  // Accessibility and SEO both depend on <html lang> being accurate.
  useEffect(() => {
    document.documentElement.lang = locale;
    applyLocaleToFormats(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    profileWriteVersion.current += 1;
    storeLocale(next);
    setLocaleState(next);
    if (userId) void supabase.from('profiles').update({ locale: next }).eq('id', userId);
  }, [userId]);

  const value = useMemo<I18nContextValue>(() => {
    const t: Translate = (key, values) =>
      translate(locale, formatLocale, key, values);

    return {
      locale,
      formatLocale,
      setLocale,
      t,
      formatNumber: (v, options) =>
        new Intl.NumberFormat(formatLocale, options).format(v),
      formatDate: (v, options) =>
        new Intl.DateTimeFormat(formatLocale, options).format(v),
      formatCurrency: (v, currency) =>
        new Intl.NumberFormat(formatLocale, { style: 'currency', currency }).format(
          v,
        ),
    };
  }, [locale, formatLocale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
